import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@medikei/ui';
import {
  Search,
  Star,
  Heart,
  Calendar,
  ChevronLeft,
  ChevronRight,
  ShieldCheck,
  X,
  RefreshCw,
  ArrowRight,
  Compass,
} from 'lucide-react';
import { favoritesApi, type FavoriteWithDoctor } from '@/api/favorites-api';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { ErrorRetry } from '@/components/ui/ErrorRetry';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/badge';

interface FavoriteDoctor {
  id: string;
  name: string;
  specialty: string;
  crm: string;
  rating: number;
  reviewCount: number;
  experience: number;
  avatarUrl?: string;
  isOnline: boolean;
  nextAvailable?: string;
  distance?: number;
}

interface FavoritesResponse {
  favorites: FavoriteDoctor[];
  total: number;
  page: number;
  pageSize: number;
}

function getDoctorInitials(name?: string | null): string {
  if (!name) return 'MD';
  const cleaned = name.replace(/^(dr\.|dra\.|dr|dra)\s+/i, '').trim();
  const parts = cleaned.split(/\s+/).filter(Boolean);
  const first = parts[0];
  const last = parts[parts.length - 1];
  if (!first) return 'MD';
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  return `${first[0] || ''}${last?.[0] || ''}`.toUpperCase();
}

export function FavoritesPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const limit = 9;

  const { data, isLoading, isError, isFetching, refetch } = useQuery<FavoritesResponse>({
    queryKey: ['patient', 'favorites', page],
    queryFn: async () => {
      try {
        const response = await favoritesApi.getFavorites();
        const favorites: FavoriteWithDoctor[] = response.favorites || [];
        return {
          favorites: favorites.map((f) => ({
            id: f.doctorId,
            name: f.doctor?.displayName || 'Médico',
            specialty: f.doctor?.specialty || 'Clínica Geral',
            crm: f.doctor?.crm ? `CRM ${f.doctor.crm}${f.doctor.crmState ? `-${f.doctor.crmState}` : ''}` : '',
            rating: f.doctor?.rating || 5.0,
            reviewCount: 0,
            experience: 0,
            avatarUrl: f.doctor?.photoUrl || undefined,
            isOnline: true,
          })),
          total: favorites.length,
          page,
          pageSize: limit,
        };
      } catch {
        return {
          favorites: [],
          total: 0,
          page,
          pageSize: limit,
        };
      }
    },
  });

  const removeFavoriteMutation = useMutation({
    mutationFn: async (doctorId: string) => {
      await favoritesApi.removeFavorite(doctorId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient', 'favorites'] });
    },
  });

  const filteredFavorites = (data?.favorites || []).filter((f) => {
    const term = search.toLowerCase().trim();
    if (!term) return true;
    return (
      f.name.toLowerCase().includes(term) ||
      f.specialty.toLowerCase().includes(term) ||
      f.crm.toLowerCase().includes(term)
    );
  });

  const totalPages = data && data.pageSize ? Math.ceil(data.total / data.pageSize) : 1;

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-foreground">
              Médicos Favoritos
            </h1>
            {data && data.total > 0 && (
              <Badge
                variant="outline"
                className="font-bold text-xs bg-primary/10 text-primary border-primary/20"
              >
                {data.total}
              </Badge>
            )}
          </div>
          <p className="text-muted-foreground text-xs sm:text-sm mt-1">
            Seus especialistas de confiança salvos para agendamentos rápidos e acompanhamento contínuo
          </p>
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <Button
            variant="outline"
            onClick={() => refetch()}
            disabled={isFetching}
            className="gap-2 rounded-xl font-semibold shadow-xs hover:bg-muted h-11 flex-1 sm:flex-initial"
          >
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin text-primary' : ''}`} />
            <span>Atualizar</span>
          </Button>

          <Button
            onClick={() => navigate('/buscar')}
            className="gap-2 rounded-xl font-bold shadow-md shadow-primary/20 h-11 flex-1 sm:flex-initial"
          >
            <Compass className="w-4 h-4" />
            <span>Buscar Médicos</span>
          </Button>
        </div>
      </div>

      {/* Barra de Busca */}
      <div className="relative max-w-md">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input
          type="text"
          placeholder="Buscar por nome, especialidade ou CRM..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-10 py-2.5 rounded-xl border border-input bg-card text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all shadow-xs"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground rounded-lg transition-colors"
            aria-label="Limpar busca"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Estados de Carregamento, Erro ou Vazio */}
      {isLoading ? (
        <LoadingOverlay message="Carregando favoritos..." />
      ) : isError ? (
        <ErrorRetry message="Erro ao carregar seus médicos favoritos" onRetry={() => refetch()} />
      ) : filteredFavorites.length === 0 ? (
        <Card className="rounded-2xl border border-dashed border-border p-8 sm:p-12 text-center bg-card">
          <div className="w-16 h-16 rounded-2xl bg-red-500/10 border border-red-500/20 text-red-500 flex items-center justify-center mx-auto mb-4 shadow-xs">
            <Heart className="w-8 h-8" />
          </div>
          <h3 className="text-lg font-bold text-foreground mb-1">
            {search ? 'Nenhum médico encontrado' : 'Nenhum médico favorito ainda'}
          </h3>
          <p className="text-xs sm:text-sm text-muted-foreground max-w-md mx-auto mb-6">
            {search
              ? `Não encontramos nenhum médico correspondente a "${search}". Tente buscar por outro termo.`
              : 'Encontre profissionais de excelência e clique no ícone de coração no perfil deles para salvá-los aqui.'}
          </p>
          {search ? (
            <Button
              variant="outline"
              onClick={() => setSearch('')}
              className="rounded-xl font-semibold"
            >
              Limpar busca
            </Button>
          ) : (
            <Button
              onClick={() => navigate('/buscar')}
              className="rounded-xl font-bold gap-2 shadow-md shadow-primary/20"
            >
              <Compass className="w-4 h-4" />
              Explorar Médicos Disponíveis
            </Button>
          )}
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredFavorites.map((doctor) => {
              const initials = getDoctorInitials(doctor.name);
              const isRemoving =
                removeFavoriteMutation.isPending &&
                removeFavoriteMutation.variables === doctor.id;

              return (
                <Card
                  key={doctor.id}
                  className="rounded-2xl border border-border bg-card shadow-xs hover:shadow-md hover:border-primary/40 transition-all overflow-hidden flex flex-col justify-between"
                >
                  <CardContent className="p-5 sm:p-6">
                    {/* Top: Avatar, Nome, CRM e Botão de Remover */}
                    <div className="flex items-start justify-between gap-3 mb-4">
                      <div
                        onClick={() => navigate(`/medicos/${doctor.id}`)}
                        className="flex items-center gap-3 min-w-0 cursor-pointer group"
                      >
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/25 via-primary/10 to-primary/5 border border-primary/25 flex items-center justify-center text-primary font-extrabold text-lg shrink-0 shadow-xs group-hover:scale-105 transition-transform overflow-hidden">
                          {doctor.avatarUrl ? (
                            <img
                              src={doctor.avatarUrl}
                              alt={doctor.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span>{initials}</span>
                          )}
                        </div>

                        <div className="min-w-0">
                          <h3 className="font-bold text-base sm:text-lg text-foreground truncate group-hover:text-primary transition-colors">
                            {doctor.name}
                          </h3>
                          <p className="text-xs sm:text-sm font-semibold text-primary truncate mt-0.5">
                            {doctor.specialty}
                          </p>
                          {doctor.crm && (
                            <span className="text-[11px] text-muted-foreground block truncate mt-0.5">
                              {doctor.crm}
                            </span>
                          )}
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => removeFavoriteMutation.mutate(doctor.id)}
                        disabled={isRemoving}
                        className="p-2 text-red-500 hover:bg-red-500/10 rounded-xl transition-colors shrink-0 cursor-pointer shadow-xs border border-transparent hover:border-red-500/20"
                        title="Remover dos favoritos"
                        aria-label="Remover dos favoritos"
                      >
                        <Heart
                          className={`h-5 w-5 fill-red-500 text-red-500 ${
                            isRemoving ? 'opacity-40 animate-pulse' : ''
                          }`}
                        />
                      </button>
                    </div>

                    {/* Classificação / Avaliação */}
                    <div className="flex items-center gap-2 mb-4 bg-muted/30 p-2.5 rounded-xl border border-border/40 text-xs">
                      <div className="flex items-center gap-1 text-amber-500">
                        <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                        <span className="font-bold text-foreground">
                          {doctor.rating.toFixed(1)}
                        </span>
                      </div>
                      <span className="text-muted-foreground">&bull; Especialista Verificado</span>
                      <ShieldCheck className="h-3.5 w-3.5 text-emerald-500 ml-auto shrink-0" />
                    </div>

                    {/* Botões de Ação */}
                    <div className="flex items-center gap-2 pt-2 border-t border-border/60">
                      <Button
                        onClick={() => navigate(`/medicos/${doctor.id}`)}
                        className="flex-1 gap-2 rounded-xl font-bold shadow-md shadow-primary/20 h-10 text-xs sm:text-sm"
                      >
                        <Calendar className="h-4 w-4" />
                        Agendar
                      </Button>

                      <Button
                        variant="outline"
                        onClick={() => navigate(`/medicos/${doctor.id}`)}
                        className="rounded-xl px-3.5 h-10 text-xs font-semibold gap-1 hover:bg-muted"
                        title="Ver perfil completo"
                      >
                        <span>Perfil</span>
                        <ArrowRight className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Paginação */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="rounded-xl font-medium"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Anterior
              </Button>
              <span className="px-3.5 py-1.5 bg-primary/10 text-primary font-bold text-xs rounded-xl border border-primary/20">
                Página {page} de {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="rounded-xl font-medium"
              >
                Próximo
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
